// 링(충격파 확장): 바닥에 눕은 고리가 from×length에서 length(반경 m)까지 빠르게 퍼지며 사라진다. 바깥 가장자리가 가장 밝고 안쪽은 꼬리처럼 흐려진다.
// 쓰는 칸: length = 최종 반경(m), from = 시작 반경 비율, width = 띠 두께(m), y = 바닥 높이, life·color·color2·intensity·seed. 드로우콜 1.
import * as THREE from 'three/webgpu';
import { float, mix, smoothstep, texture, uv, vec2 } from 'three/tsl';
import { BatchModule, fxAttributes, type Slot } from './batch';
import { stripLayout } from './layout';
import { clamp01, easeOut } from './path';
import { MODULE_CAPS, type ModuleKind } from './types';

export const RING_SEG = 48;
const SIN = new Float32Array(RING_SEG + 1);
const COS = new Float32Array(RING_SEG + 1);
for (let i = 0; i <= RING_SEG; i++) {
  const a = (i / RING_SEG) * Math.PI * 2;
  SIN[i] = Math.sin(a);
  COS[i] = Math.cos(a);
}
/** 바닥보다 살짝 위(m): 바닥과 깊이 싸움을 피한다 */
const RING_LIFT = 0.06;

function makeMaterial(noise: THREE.Texture): THREE.MeshBasicNodeMaterial {
  const { ctl, t1, t2 } = fxAttributes();
  const age = ctl.x, env = ctl.y, seed = ctl.z;
  const u = uv().x, v = uv().y;
  // v: 안쪽 0 → 바깥 1. 앞 가장자리(바깥)가 날카롭고 뒤(안쪽)는 흐리다
  const front = v.pow(2.2).mul(float(1).sub(smoothstep(0.9, 1.0, v)));
  // 각도 방향 끊김: 균일한 고리가 아니라 부서진 파동
  const n = texture(noise, vec2(u.mul(3).add(seed), age.mul(0.4))).r;
  const alpha = front.mul(n.mul(0.9).add(0.35)).mul(env);
  const col = mix(t1.rgb, t2.rgb, front.pow(3)).mul(t1.w);

  const m = new THREE.MeshBasicNodeMaterial();
  m.transparent = true;
  m.depthWrite = false;
  m.side = THREE.DoubleSide;
  m.blending = THREE.AdditiveBlending;
  m.forceSinglePass = true;
  m.fog = false;
  m.colorNode = col;
  m.opacityNode = alpha;
  return m;
}

export class RingModule extends BatchModule {
  readonly kind: ModuleKind = 'ring';

  constructor(noise: THREE.Texture, cap = MODULE_CAPS.ring) {
    super(cap, stripLayout(RING_SEG), makeMaterial(noise), 18, 'fx-ring');
  }

  protected positions(s: Slot, out: Float32Array, o: number): void {
    const age01 = clamp01(s.age / s.life);
    const R = s.length * s.scale;
    const r = R * (s.from + (1 - s.from) * easeOut(age01));
    // 띠 두께는 처음엔 두껍고 퍼질수록 얇아진다
    const w = Math.min(r * 0.9, s.width * s.scale * (1 - 0.55 * age01));
    const r0 = r - w;
    const sin = Math.sin(s.yaw), cos = Math.cos(s.yaw);
    const cx = s.x + sin * s.ahead, cz = s.z + cos * s.ahead, y = s.y + RING_LIFT;
    for (let i = 0; i <= RING_SEG; i++) {
      const k = o + i * 6;
      out[k] = cx + SIN[i] * r0; out[k + 1] = y; out[k + 2] = cz + COS[i] * r0;
      out[k + 3] = cx + SIN[i] * r; out[k + 4] = y; out[k + 5] = cz + COS[i] * r;
    }
  }

  protected ctl(s: Slot, out: Float32Array): void {
    const age01 = clamp01(s.age / s.life);
    out[0] = age01;
    out[1] = Math.min(1, s.age / 0.015) * Math.pow(1 - age01, 1.3);
    out[2] = s.seed;
    out[3] = 0;
  }
}
